/**
 * **Where the refresh token lives — the one place, so it can be moved.**
 *
 * ## The ruling, and what it costs
 *
 * Sumit, 2026-09-02: *"I'm ok with local storage to avoid cross domain cookie issue."*
 * `ADR 051` §5 records it as a trade rather than a preference, and the survey's §7 establishes
 * that the alternative is not merely inconvenient but **unavailable**: the API has no custom
 * domain in any environment, production included, and is served from `execute-api.amazonaws.com`
 * — a public suffix. A refresh cookie is cross-site from both SPA origins and would be blocked
 * by Safari ITP and Firefox TCP.
 *
 * **The trap is that it works on localhost.** `http://localhost:5201` → `http://localhost:5200`
 * is same-site (cookies ignore port), so an `httpOnly` refresh cookie behaves perfectly on every
 * developer's laptop and fails in every deployed environment. A prototype proves nothing here.
 *
 * What this costs, stated plainly: **an XSS on any console can read a 30-day credential.** What
 * bounds it is written in `ADR 051` §5 — short access tokens, `RevokeToken` biting within one
 * access-token lifetime, and both credentials cleared on every failure route.
 *
 * ## Why this file exists rather than a second `localStorage.getItem`
 *
 * The decision is expected to be revisited: `ADR 051` says so, and survey §7.6 lists exactly what
 * giving the API a hostname under `h2hspark.com` takes (the wildcard certificate already covers
 * it). So the storage is **one seam with three methods**, not a key spelled out at each call
 * site. Moving to an `httpOnly` cookie means writing a second implementation of
 * {@link RefreshCredentialStore} whose `read` returns null and whose `write` is a no-op — the
 * browser holds the credential and `/auth/session/refresh` reads it from the request — and
 * changing nothing else.
 *
 * The access token deliberately does **not** move behind this seam. It has to be readable by the
 * client to be put in an `Authorization` header, so it can never live in an `httpOnly` cookie,
 * and pretending the two credentials have the same future would make the seam a lie.
 */
/** localStorage key the refresh credential is persisted under. */
export const REFRESH_TOKEN_STORAGE_KEY = 'auth.refreshToken';
/**
 * The store as it ships today: one `localStorage` key, every access wrapped.
 *
 * `localStorage` throws outright in a Safari private window and under some enterprise policies —
 * not returns null, throws — so each of the three operations swallows. Six of the seven download
 * call sites `PLT-014` AC-6 replaced read storage with no `try`/`catch`, which is why this is
 * spelled out rather than assumed.
 */
export function browserRefreshCredentialStore(storage) {
    return {
        read() {
            if (storage === null)
                return null;
            try {
                const value = storage.getItem(REFRESH_TOKEN_STORAGE_KEY);
                return value === null || value.length === 0 ? null : value;
            }
            catch {
                return null;
            }
        },
        write(token) {
            if (storage === null || token.length === 0)
                return;
            try {
                storage.setItem(REFRESH_TOKEN_STORAGE_KEY, token);
            }
            catch {
                // A session that cannot persist its refresh credential still works until the access
                // token expires; refusing the login over it would be worse.
            }
        },
        clear() {
            if (storage === null)
                return;
            try {
                storage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
            }
            catch {
                // Nothing useful to do; the credential is unreachable either way.
            }
        },
    };
}
/** A store that holds nothing. The default when a host supplies no storage (SSR, tests). */
export function noRefreshCredentialStore() {
    return {
        read: () => null,
        write: () => undefined,
        clear: () => undefined,
    };
}
//# sourceMappingURL=refresh-credential-store.js.map